import { computed, inject, Injectable, SecurityContext } from '@angular/core';
import { defer, map, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, share, switchMap, take, tap } from 'rxjs/operators';
import { IrisAuthService } from '@iris/modules/auth/utils/auth.service';
import { TokenStorage } from '@iris/modules/auth/utils/token-storage';
import { LoginResult } from '@iris/modules/auth/utils/login-result';
import { IrisEnvironmentService } from '@iris/common/services/environment.service';
import { SWService } from '@iris/common/services/service-worker.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { LoggerService } from '@iris/common/modules/rum/logger.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthEventManager } from '@iris/modules/auth/utils/auth.event-manager';

@Injectable({ providedIn: 'root' })
export class AuthFacade {
  private readonly authService = inject(IrisAuthService);
  private readonly environmentService = inject(IrisEnvironmentService);
  private readonly swService = inject(SWService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly loggerService = inject(LoggerService);

  private readonly authEventManager = inject(AuthEventManager);

  private readonly tokenStorage = new TokenStorage(this.environmentService.env.instanceId);
  readonly accessTokenExpiredAt = computed(() => this.tokenStorage.accessTokenExpiredAt());

  lastHref: string;

  private activeRefresh$: Observable<string>;
  private tokenRefreshTimeout: ReturnType<typeof setTimeout>;

  constructor() {
    this.swService.authSWInit$.pipe(
      switchMap(() => this.actualToken()),
      tap((token) => {
        // eslint-disable-next-line no-console
        console.info(`[IRIS] Set token to service worker on init: ${Boolean(token)}`);
        if (token) { this.swService.updateToken(token); }
      }),
      takeUntilDestroyed(),
    ).subscribe();
  }

  actualToken(forceRefresh = false): Observable<string> {
    if (!forceRefresh && !this.accessTokenExpired() && this.isWellFormattedToken(this.tokenStorage.accessToken)) {
      return of(this.tokenStorage.accessToken);
    }

    if (this.tokenStorage.refreshToken) {
      return this.requestTokenRefresh();
    }

    return of(this.tokenStorage.accessToken);
  }

  isLoggedIn(): boolean {
    return !!this.tokenStorage.accessToken;
  }

  accessTokenExpired(): boolean {
    return !this.tokenStorage?.accessTokenIsNotExpired();
  }

  private processLoginResult(loginResult: LoginResult): Observable<LoginResult> {
    this.tokenStorage.setLoginResult(loginResult);
    return this.setupToken(loginResult?.accessToken).pipe(
      map(() => loginResult),
    );
  }

  requestTokenRefresh(): Observable<string> {
    const { accessToken, refreshToken } = this.getTokensData();

    if (!refreshToken) {
      this.tokenStorage.clear();
      this.logout();
      return of(null);
    }

    this.activeRefresh$ ??= defer(() => {
      // eslint-disable-next-line no-console
      console.info(`[IRIS] Token refresh requested`);
      return this.authService.refreshToken(refreshToken).pipe(
        switchMap((loginResult) => {
          if ( !this.isWellFormattedToken(loginResult?.accessToken)) {
            return throwError(() => new Error('Invalid token'));
          }
          return this.processLoginResult(loginResult);
        }),
        catchError((error: HttpErrorResponse) => {
          const message = `Message: User token refresh | Error Status: ${error.status} | RefreshToken: ${refreshToken} | AccessToken: ${accessToken}`;
          this.tokenStorage.clear();
          void this.loggerService.logMessageToKibana(message, 'Authorization error').then(() => {
            this.logout();
          });
          return of(null);
        }),
        map((loginResult) => loginResult?.accessToken),
        finalize(() => this.activeRefresh$ = null),
        share({ resetOnRefCountZero: false }),
      );
    });
    return this.activeRefresh$;
  }

  loginUsername(username: string, password: string): Observable<LoginResult> {
    return this.authService.loginUsername(username, password).pipe(
      switchMap((loginResult) => this.processLoginResult(loginResult)),
      catchError(() => {
        this.tokenStorage.clear();
        return throwError(() => new Error('error.UserAuthFailed'));
      }),
    );
  }

  impersonateUserById(userId: number): Observable<LoginResult> {
    return this.authService.impersonateUserById(userId).pipe(
      switchMap((loginResult) => this.processLoginResult(loginResult)),
      catchError(() => {
        return throwError(() => new Error('error.UserAuthFailed'));
      }),
    );
  }

  loginAzure(azureAccessToken: string): Observable<LoginResult> {
    return this.authService.loginAzure(azureAccessToken).pipe(
      switchMap((loginResult) => this.processLoginResult(loginResult)),
      catchError(() => {
        this.tokenStorage.clear();
        return throwError(() => new Error('error.UserAuthFailed'));
      }),
    );
  }

  setupToken(accessToken: string): Observable<string> {
    if (this.tokenRefreshTimeout) {
      clearTimeout(this.tokenRefreshTimeout);
      this.tokenRefreshTimeout = null;
    }

    if (!accessToken) {
      return of(accessToken);
    }

    this.handleTokenExpiration();
    return this.shareToken(accessToken);
  }

  private handleTokenExpiration(): void {
    const tokenExpiresIn = this.tokenStorage.accessTokenExpiredAt() - Date.now();
    if (tokenExpiresIn > 0) {
      // eslint-disable-next-line no-console
      console.info(`[IRIS] Auth token expires in ${Math.floor(tokenExpiresIn / 1000)} seconds`);
      this.tokenRefreshTimeout = setTimeout(() => {
        this.authEventManager.emitEvent('TokenExpired');
      }, tokenExpiresIn);
    }
  }

  private shareToken(accessToken: string): Observable<string> {
    // eslint-disable-next-line no-console
    console.info(`[IRIS] Sharing token with external services: ${Boolean(accessToken)}`);

    if (accessToken) {
      this.swService.updateToken(accessToken);
      return this.authService.updateNodeToken(accessToken).pipe(
        map(() => accessToken));
    }
    return of(accessToken);
  }

  logout(isBackLogout = false): void {
    this.authService.updateNodeToken().pipe(
      switchMap(() => isBackLogout && this.isLoggedIn() ? this.authService.logout() : of(null)),
      take(1),
      tap(() => {
        this.tokenStorage.clear();
        sessionStorage.removeItem('skipVersionModal');
        location.reload();
      }),
    ).subscribe();
  }

  getTokensData(): { accessToken: string; refreshToken: string } {
    return {
      accessToken: this.tokenStorage.accessToken,
      refreshToken: this.tokenStorage.refreshToken,
    };
  }

  getSafeUrl(url: string): string {
    const sanitizedUrl = this.sanitizer.sanitize(SecurityContext.URL, url) || '';
    return sanitizedUrl.startsWith('unsafe') ? undefined : sanitizedUrl;
  }

  isWellFormattedToken(token: string): boolean {
    return !!token && token.split('.').length === 3;
  }
}
